// Copyright 2025 Greener Power Solutions
// This file is part of the Greener Power Solutions,
// and may not be altered, copied, redistributed, or sold
// without specific permission.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Socket, io } from 'socket.io-client';
import { WEBSOCKET_URL } from '../../constants';
import useAuth from '../useAuth';

interface ChatSocketConnectionState {
  socket: Socket | null;
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
}

interface UseChatSocketConnectionReturn extends ChatSocketConnectionState {
  reconnect: () => void;
  disconnect: () => void;
}

/**
 * Hook for managing socket.io connection to the /chat namespace
 * Handles authentication, connection state, and reconnection logic
 */
export const useChatSocketConnection = (): UseChatSocketConnectionReturn => {
  const { getTokenSilently } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const connect = useCallback(async () => {
    // console.log("useChatSocketConnection.connect")
    setIsConnecting(true);
    setError(null);

    try {
      const token = await getTokenSilently();
      const url = `${WEBSOCKET_URL.replace(/\/$/, '')}/chat`; // Ensures only one slash between baseUrl and namespace

      const newSocket = io(url, {
        path: '/socket/socket.io',
        auth: { accessToken: token },
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 2000,
        ackTimeout: 10000,
        transports: ['websocket', 'polling'],
      });

      // Set up event listeners
      newSocket.on('connect', () => {
        setIsConnected(true);
        setIsConnecting(false);
        setError(null);
        clearReconnectTimeout();
      });

      newSocket.on('disconnect', (reason) => {
        setIsConnected(false);
        setIsConnecting(false);

        // Only attempt manual reconnection for certain disconnect reasons
        if (reason !== 'transport close' && reason !== 'io client disconnect') {
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 5000);
        }
      });

      newSocket.on('connect_error', (err) => {
        setIsConnected(false);
        setIsConnecting(false);
        setError(err.message || 'Connection failed');
      });

      setSocket(newSocket);
    } catch (err) {
      setIsConnecting(false);
      setError(err instanceof Error ? err.message : 'Authentication failed');
    }
  }, [getTokenSilently, clearReconnectTimeout]);

  const disconnect = useCallback(() => {
    // console.log("useChatSocketConnection.disconnect")
    clearReconnectTimeout();
    socket?.disconnect();
    setSocket(null);
    setIsConnected(false);
    setIsConnecting(false);
    setError(null);
  }, [socket, clearReconnectTimeout]);

  const reconnect = useCallback(() => {
    // console.log("useChatSocketConnection.reconnect")
    disconnect();
    connect();
  }, [disconnect, connect]);

  // Initial connection
  useEffect(() => {
    // console.log("useChatSocketConnection.init")
    connect();
  }, [connect]);

  // Recover the connection when the tab becomes visible again or the browser
  // regains connectivity. After long inactivity (e.g. the machine sleeping)
  // socket.io exhausts its reconnection attempts and the cached access token
  // expires, leaving a silently dead socket. Rebuilding it with a freshly
  // fetched token restores the chat without a manual page refresh.
  const isConnectedRef = useRef(isConnected);
  const isConnectingRef = useRef(isConnecting);
  const reconnectRef = useRef(reconnect);

  useEffect(() => {
    isConnectedRef.current = isConnected;
    isConnectingRef.current = isConnecting;
    reconnectRef.current = reconnect;
  }, [isConnected, isConnecting, reconnect]);

  useEffect(() => {
    const recoverIfNeeded = () => {
      if (document.visibilityState === 'visible' && !isConnectedRef.current && !isConnectingRef.current) {
        reconnectRef.current();
      }
    };

    document.addEventListener('visibilitychange', recoverIfNeeded);
    window.addEventListener('online', recoverIfNeeded);

    return () => {
      document.removeEventListener('visibilitychange', recoverIfNeeded);
      window.removeEventListener('online', recoverIfNeeded);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // console.log("useChatSocketConnection.cleanup")
      clearReconnectTimeout();
      socket?.disconnect();
    };
  }, [socket, clearReconnectTimeout]);

  return {
    socket,
    isConnected,
    isConnecting,
    error,
    reconnect,
    disconnect,
  };
};
